import { isEqual } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import {
  CallersByClientTable,
  ClientMTDOverviewTable,
  TabContent,
} from '../../components/EODReport';
import { DATE_TIME_FORMAT, TAB_ID_MAP } from '../../constants';
import {
  getFormattedDateInMomentDefaultTZ,
  getNowInDefaultTimezoneAsDate,
  saveMultiPagePDFFile,
} from '../../helpers';
import { Layout } from '../../layouts';

const getFormattedFilters = filters => {
  const reportDate = getFormattedDateInMomentDefaultTZ(
    filters.selectedDate,
  ).format(DATE_TIME_FORMAT.YYYY_MM_DD_DASH);
  const adminTeam = filters.clientIds.join(';');

  return {
    reportDate,
    ...(adminTeam && { adminTeam }),
  };
};

const INITIAL_ACTIVE_TAB = TAB_ID_MAP.CLIENT_MTD_OVERVIEW;

export const EODReport = () => {
  const clientMTDOverviewAbortControllerRef = useRef(null);
  const callersByClientAbortControllerRef = useRef(null);

  const [clients, setClients] = useState([]);
  const [isClientsLoading, setIsClientsLoading] = useState(true);
  const [isClientsError, setIsClientsError] = useState(false);
  const [clientMTDOverview, setClientMTDOverview] = useState([]);
  const [isClientMTDOverviewLoading, setIsClientMTDOverviewLoading] =
    useState(true);
  const [isClientMTDOverviewError, setIsClientMTDOverviewError] =
    useState(false);
  const [callersByClient, setCallersByClient] = useState([]);
  const [isCallersByClientLoading, setIsCallersByClientLoading] =
    useState(true);
  const [isCallersByClientError, setIsCallersByClientError] = useState(false);
  const [activeTab, setActiveTab] = useState(INITIAL_ACTIVE_TAB);

  const initialClientMTDOverviewFilters = {
    clientIds: [],
    selectedDate: getNowInDefaultTimezoneAsDate(),
  };
  const initialCallersByClientFilters = {
    clientIds: [],
    selectedDate: getNowInDefaultTimezoneAsDate(),
  };

  const [clientMTDOverviewFilters, setClientMTDOverviewFilters] = useState(
    initialClientMTDOverviewFilters,
  );
  const [callersByClientFilters, setCallersByClientFilters] = useState(
    initialCallersByClientFilters,
  );

  const formattedClientMTDOverviewFilters = useMemo(
    () => getFormattedFilters(clientMTDOverviewFilters),
    [clientMTDOverviewFilters],
  );
  const formattedCallersByClientFilters = useMemo(
    () => getFormattedFilters(callersByClientFilters),
    [callersByClientFilters],
  );

  const fetchClients = useCallback(async () => {
    setIsClientsLoading(true);
    setIsClientsError(false);

    try {
      const response = await fetch('/admin/get-active-clients');
      if (response.status !== 200) throw new Error();
      const data = await response.json();
      setClients(data.clients);
      setIsClientsLoading(false);
    } catch {
      setIsClientsLoading(false);
      setIsClientsError(true);
      toast.error('Failed to load clients!', {
        position: 'top-right',
        autoClose: 3000,
      });
    }
  }, []);

  const fetchClientMTDOverview = useCallback(async () => {
    setIsClientMTDOverviewLoading(true);
    setIsClientMTDOverviewError(false);

    if (clientMTDOverviewAbortControllerRef.current) {
      clientMTDOverviewAbortControllerRef.current.abort();
    }

    clientMTDOverviewAbortControllerRef.current = new AbortController();

    try {
      const response = await fetch('/admin/eod-reports/client-mtd-overview', {
        signal: clientMTDOverviewAbortControllerRef.current.signal,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formattedClientMTDOverviewFilters),
      });
      if (response.status !== 200) throw new Error();
      const data = await response.json();
      setClientMTDOverview(data.data);
      setIsClientMTDOverviewLoading(false);
    } catch (e) {
      if (e?.name === 'AbortError') return;
      setIsClientMTDOverviewLoading(false);
      setIsClientMTDOverviewError(true);
    }
  }, [formattedClientMTDOverviewFilters]);

  const fetchCallersByClient = useCallback(async () => {
    setIsCallersByClientLoading(true);
    setIsCallersByClientError(false);

    if (callersByClientAbortControllerRef.current) {
      callersByClientAbortControllerRef.current.abort();
    }

    callersByClientAbortControllerRef.current = new AbortController();

    try {
      const response = await fetch('/admin/eod-reports/callers-by-client', {
        signal: callersByClientAbortControllerRef.current.signal,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formattedCallersByClientFilters),
      });
      if (response.status !== 200) throw new Error();
      const data = await response.json();
      setCallersByClient(data.data);
      setIsCallersByClientLoading(false);
    } catch (e) {
      if (e?.name === 'AbortError') return;
      setIsCallersByClientLoading(false);
      setIsCallersByClientError(true);
    }
  }, [formattedCallersByClientFilters]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  useEffect(() => {
    fetchClientMTDOverview();
  }, [fetchClientMTDOverview]);

  useEffect(() => {
    fetchCallersByClient();
  }, [fetchCallersByClient]);

  const tabProps = {
    [TAB_ID_MAP.CLIENT_MTD_OVERVIEW]: {
      filters: clientMTDOverviewFilters,
      isFiltersChanged: !isEqual(
        getFormattedFilters(initialClientMTDOverviewFilters),
        formattedClientMTDOverviewFilters,
      ),
      onChangeFilters: setClientMTDOverviewFilters,
      resetFilters: () => {
        setClientMTDOverviewFilters(initialClientMTDOverviewFilters);
      },
      tableData: clientMTDOverview,
      isTableDataLoading: isClientMTDOverviewLoading,
      isTableDataError: isClientMTDOverviewError,
      refetchTableData: fetchClientMTDOverview,
      renderTable: data => <ClientMTDOverviewTable data={data} />,
      onExportPDF: async data =>
        await saveMultiPagePDFFile(
          <ClientMTDOverviewTable data={data} pdfMode />,
          'Client MTD Overview Report.pdf',
        ),
    },
    [TAB_ID_MAP.CALLERS_BY_CLIENT]: {
      filters: callersByClientFilters,
      isFiltersChanged: !isEqual(
        getFormattedFilters(initialCallersByClientFilters),
        formattedCallersByClientFilters,
      ),
      onChangeFilters: setCallersByClientFilters,
      resetFilters: () => {
        setCallersByClientFilters(initialCallersByClientFilters);
      },
      tableData: callersByClient,
      isTableDataLoading: isCallersByClientLoading,
      isTableDataError: isCallersByClientError,
      refetchTableData: fetchCallersByClient,
      renderTable: data => <CallersByClientTable data={data} />,
      onExportPDF: async data =>
        await saveMultiPagePDFFile(
          <CallersByClientTable data={data} pdfMode />,
          'Callers by Client Report.pdf',
        ),
    },
  };

  return (
    <Layout>
      <TabContent
        activeTab={activeTab}
        onChangeActiveTab={setActiveTab}
        clients={clients}
        isClientsLoading={isClientsLoading}
        isClientsError={isClientsError}
        {...tabProps[activeTab]}
      />
    </Layout>
  );
};
